import type { JSX } from 'react';
import { addToast } from '@heroui/react';

import { useRemoveTeamMember } from '~/hooks/useTeamMembers';
import { getDisplayName } from '~/utils/member';

import { ConfirmationDialog } from '~/components/ConfirmationDialog';

import type { TeamMember } from '~/types';

interface RemoveTeamMemberConfirmationProps {
  isOpen: boolean;
  onClose: () => void;
  member: TeamMember;
  teamId: string;
  teamName: string;
}

export function RemoveTeamMemberConfirmation(
  props: RemoveTeamMemberConfirmationProps,
): JSX.Element {
  const { isOpen, member, onClose, teamId, teamName } = props;
  const { isPending, mutate } = useRemoveTeamMember();

  const handleRemove = (): void => {
    mutate(
      {
        teamId,
        userId: member.user_id,
      },
      {
        onSuccess: () => {
          addToast({
            description: `${getDisplayName(member)} has been removed from ${teamName}`,
            color: 'success',
          });
          onClose();
        },
        onError: (error: Error) => {
          let description = error.message || 'Failed to remove team member.';

          if (error.message.includes('last') || error.message.includes('Cannot remove')) {
            description = 'Cannot remove the last team admin.';
          } else if (error.message.includes('403')) {
            description = 'You do not have permission to remove team members.';
          }

          addToast({
            description,
            color: 'danger',
          });
        },
      },
    );
  };

  const handleClose = (): void => {
    if (!isPending) {
      onClose();
    }
  };

  return (
    <ConfirmationDialog
      content={
        <>
          <p>
            Are you sure you want to remove{' '}
            <span className="font-semibold">{getDisplayName(member)}</span> from{' '}
            <span className="font-semibold">{teamName}</span>?
          </p>

          <p className="mt-1 text-sm text-muted">
            This action cannot be undone. The user will lose access to the team immediately.
          </p>
        </>
      }
      isOpen={isOpen}
      isPending={isPending}
      onClose={handleClose}
      onConfirm={handleRemove}
      title="Remove Team Member"
    />
  );
}
